/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, BarChart3, Check, ChevronRight, Clock, FileText, Layers, Lightbulb, MessageCircle, PieChart, Shield, Sparkles,
  TrendingDown, TrendingUp, Wallet, Zap,
} from 'lucide-react';
import { generateAnalysis, fetchReports, setCurrentReport, clearCurrentReport } from '../store/slices/analysisSlice';
import { fetchPortfolio } from '../store/slices/portfolioSlice';
import { fetchSettings } from '../store/slices/authSlice';
import { useToast } from '../contexts/ToastContext';
import { useShell } from '../components/shell/ShellContext';
import { BrokerGate } from '../components/shell/Broker';
import { MarkdownProse } from '../components/markdown/MarkdownProse';
import { AllocationDonut, PLBars, withColors } from '../components/charts';
import palette from '../theme/palette';
import { cx } from '../theme/cx';
import { formatDateTime } from '../theme/format';
import { Banner, Button, Card, CardHeader, EmptyState, IconTile, PageHeader, RadioCards } from '../components/ui';

const getSector = (symbol, description, assetType) => {
  const desc = (description || '').toLowerCase();
  const sym = (symbol || '').toLowerCase();

  // ETFs
  if (assetType === 'Etf' || desc.includes('etf') || desc.includes('ucits')) {
    if (desc.includes('defense') || desc.includes('defence')) return 'ETF défense';
    if (desc.includes('s&p 500') || desc.includes('sp500')) return 'ETF indice US';
    if (desc.includes('europe') || desc.includes('euro')) return 'ETF Europe';
    return 'ETF';
  }

  // Energy/Oil & Gas
  if (desc.includes('oil') || desc.includes('gas') || desc.includes('petrol') ||
      desc.includes('energy') || desc.includes('repsol') || desc.includes('saipem') ||
      desc.includes('maurel') || desc.includes('viridien') || sym.includes('rep') ||
      sym.includes('maup') || sym.includes('viri') || sym.includes('spmi')) {
    return 'Énergie';
  }

  // Defense/Aerospace
  if (desc.includes('defense') || desc.includes('defence') || desc.includes('aerospace') ||
      desc.includes('exail') || sym.includes('exa')) {
    return 'Défense';
  }

  // Healthcare/Biotech
  if (desc.includes('biotech') || desc.includes('pharma') || desc.includes('health') ||
      desc.includes('sartorius') || sym.includes('dim')) {
    return 'Santé';
  }

  // Technology/Telecom
  if (desc.includes('tech') || desc.includes('software') || desc.includes('digital') ||
      desc.includes('ekinops') || desc.includes('vusion') || desc.includes('lectra') ||
      sym.includes('eki') || sym.includes('vu') || sym.includes('lss')) {
    return 'Technologie';
  }

  // Industrial/Materials
  if (desc.includes('industrial') || desc.includes('steel') || desc.includes('metal') ||
      desc.includes('tubacex') || desc.includes('mersen') || desc.includes('assystem') ||
      sym.includes('tub') || sym.includes('cblp') || sym.includes('asy')) {
    return 'Industrie';
  }

  return 'Autres';
};

const ANALYSIS_TYPES = [
  { value: 'comprehensive', label: 'Analyse complète', icon: FileText, description: 'Performance, risques et pistes d’amélioration sur l’ensemble du portefeuille.' },
  { value: 'risk', label: 'Évaluation des risques', icon: Shield, description: 'Concentration, volatilité et expositions à surveiller.' },
  { value: 'opportunities', label: 'Opportunités', icon: Lightbulb, description: 'Pistes de croissance et lignes à renforcer.' },
  { value: 'diversification', label: 'Diversification', icon: PieChart, description: 'Équilibre sectoriel et géographique du PEA.' },
  { value: 'alpha', label: 'Alpha maximal', icon: Zap, description: 'Approche offensive orientée surperformance.' },
];

const PROVIDERS = { openai: 'OpenAI', gemini: 'Google Gemini' };

const BENEFITS = [
  'Analyse détaillée de la performance',
  'Évaluation des risques et recommandations',
  'Tendances et contexte de marché',
  'Pistes d’action concrètes',
];

const Fact = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
    <dt className="text-sm text-fg-muted">{label}</dt>
    <dd className="text-right text-sm font-medium text-fg">{children}</dd>
  </div>
);

const PortfolioCharts = ({ positions, summary }) => {
  const sectors = useMemo(() => {
    const totals = positions.reduce((acc, p) => {
      const sector = getSector(p.symbol, p.description, p.asset_type);
      acc[sector] = (acc[sector] || 0) + Math.abs(p.market_value || p.amount * p.open_price);
      return acc;
    }, {});
    return withColors(Object.entries(totals).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0).sort((a, b) => b.value - a.value));
  }, [positions]);

  const plData = useMemo(() => positions
    .filter((p) => p.profit_loss !== 0)
    .sort((a, b) => b.profit_loss - a.profit_loss)
    .slice(0, 10)
    .map((p) => ({ name: p.symbol?.split(':')[0] || p.symbol, value: p.profit_loss })), [positions]);

  const totalInvested = positions.reduce((sum, p) => sum + (p.amount * p.open_price), 0);
  const cash = summary?.cash_balance || 0;
  const totalValue = summary?.total_value || totalInvested;
  const cashSplit = [
    { name: 'Investi', value: totalValue - cash, color: palette.chart.gold },
    { name: 'Liquidités', value: cash, color: palette.chart.cash },
  ].filter((d) => d.value > 0);
  const winners = positions.filter((p) => p.profit_loss > 0).length;
  const losers = positions.filter((p) => p.profit_loss < 0).length;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader icon={PieChart} title="Répartition sectorielle" description="Par valorisation des lignes" />
        {sectors.length ? <AllocationDonut data={sectors} centerLabel="Investi" height={200} legendLimit={sectors.length} /> : <p className="py-8 text-center text-sm text-fg-muted">Aucune position valorisée.</p>}
      </Card>
      <Card>
        <CardHeader icon={BarChart3} title="Plus/moins-values par ligne" description="Les dix écarts les plus marqués" />
        {plData.length ? <PLBars data={plData} height={Math.max(160, plData.length * 30)} /> : <p className="py-8 text-center text-sm text-fg-muted">Aucune plus ou moins-value.</p>}
      </Card>
      <Card>
        <CardHeader icon={Wallet} title="Liquidités et investi" description="Part de chaque poche dans la valeur totale" />
        {cashSplit.length ? <AllocationDonut data={cashSplit} total={totalValue} height={180} /> : <p className="py-8 text-center text-sm text-fg-muted">Aucune donnée.</p>}
      </Card>
      <Card>
        <CardHeader icon={Layers} title="Synthèse des positions" />
        <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-control border border-line bg-line">
          <div className="bg-surface-1 p-4">
            <dt className="eyebrow">Positions</dt>
            <dd className="num mt-2 text-2xl font-semibold text-fg">{positions.length}</dd>
          </div>
          <div className="bg-surface-1 p-4">
            <dt className="eyebrow flex items-center gap-1.5"><TrendingUp size={12} aria-hidden="true" />En gain</dt>
            <dd className="num mt-2 text-2xl font-semibold text-gain-400">{winners}</dd>
          </div>
          <div className="bg-surface-1 p-4">
            <dt className="eyebrow flex items-center gap-1.5"><TrendingDown size={12} aria-hidden="true" />En perte</dt>
            <dd className="num mt-2 text-2xl font-semibold text-loss-400">{losers}</dd>
          </div>
        </dl>
        {positions.length > 0 && (
          <div className="mt-5 flex h-2 overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
            <span className="h-full bg-gain-500" style={{ width: `${(winners / positions.length) * 100}%` }} />
            <span className="h-full bg-loss-500" style={{ width: `${(losers / positions.length) * 100}%` }} />
          </div>
        )}
      </Card>
    </div>
  );
};

const Analysis = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { openChat } = useShell();
  const { currentReport, reports, loading, error } = useSelector((state) => state.analysis);
  const { positions, saxoConnected, summary } = useSelector((state) => state.portfolio);
  const { settings } = useSelector((state) => state.auth);

  const [selectedType, setSelectedType] = useState('comprehensive');
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [fullPortfolio, setFullPortfolio] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    dispatch(fetchReports());
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (saxoConnected) {
      dispatch(fetchPortfolio());
    }
  }, [dispatch, saxoConnected]);

  const handleGenerate = async () => {
    const result = await dispatch(generateAnalysis({
      type: selectedType,
      symbols: fullPortfolio ? [] : selectedSymbols,
      full_portfolio: fullPortfolio,
      focus: selectedType === 'comprehensive' ? '' : selectedType,
    }));
    if (generateAnalysis.fulfilled.match(result)) toast.success('Analyse prête', { description: result.payload?.title });
  };

  const toggleSymbol = (symbol) => {
    setSelectedSymbols((current) => (current.includes(symbol) ? current.filter((s) => s !== symbol) : [...current, symbol]));
  };

  const discuss = () => {
    const { title, content = '' } = currentReport;
    openChat(`Je viens de générer ce rapport d'analyse : « ${title} »\n\nVoici l'analyse :\n${content.substring(0, 3000)}${content.length > 3000 ? '…' : ''}\n\nApporte-moi des éclairages complémentaires, réponds à mes questions et propose les prochaines étapes.`);
  };

  const hasApiKey = settings?.ai_api_key && settings.ai_api_key !== '********';
  const typeLabel = ANALYSIS_TYPES.find((t) => t.value === selectedType)?.label;
  const scopeOptions = [
    { value: 'full', label: 'Portefeuille complet', icon: Layers, description: `Analyser les ${positions.length} positions` },
    { value: 'selection', label: 'Sélection de valeurs', icon: Check, description: 'Choisir les valeurs à analyser' },
  ];

  let body;
  if (showHistory) {
    body = (
      <Card>
        <CardHeader icon={Clock} title="Historique des analyses" description="Vos analyses précédentes, de la plus récente à la plus ancienne" />
        {reports.length === 0 ? (
          <EmptyState icon={FileText} description="Aucune analyse pour le moment." />
        ) : (
          <ul className="space-y-2">
            {reports.map((report) => (
              <li key={report.id}>
                <button
                  type="button"
                  onClick={() => { dispatch(setCurrentReport(report)); setShowHistory(false); }}
                  className="group flex w-full items-center gap-4 rounded-control border border-line bg-surface-2/40 p-4 text-left transition-colors hover:border-line-strong hover:bg-surface-2"
                >
                  <IconTile icon={FileText} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-fg">{report.title}</span>
                    <span className="block text-sm text-fg-subtle">{formatDateTime(report.generated_at)}</span>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" className="text-fg-subtle transition-colors group-hover:text-gold-400" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  } else if (currentReport) {
    body = (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="eyebrow mb-1 text-gold-400/90">Résultat</p>
            <h2 className="font-display text-display-md font-normal text-fg">{currentReport.title}</h2>
            <p className="mt-1 text-sm text-fg-subtle">Générée le {formatDateTime(currentReport.generated_at)}</p>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <Button variant="secondary" leadingIcon={ArrowLeft} onClick={() => dispatch(clearCurrentReport())}>Nouvelle analyse</Button>
            <Button leadingIcon={MessageCircle} onClick={discuss}>En discuter avec Gladys</Button>
          </div>
        </div>
        <PortfolioCharts positions={positions} summary={summary} />
        <Card padding="lg">
          <CardHeader icon={FileText} title="Rapport de l’analyse" description="Rédigé par l’IA à partir de vos positions" />
          <div className="mx-auto max-w-[72ch]">
            <MarkdownProse variant="analysis">{currentReport.content}</MarkdownProse>
          </div>
        </Card>
      </div>
    );
  } else {
    body = (
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Type d’analyse" description="L’angle que l’IA doit privilégier" />
            <RadioCards ariaLabel="Type d’analyse" options={ANALYSIS_TYPES} value={selectedType} onChange={setSelectedType} className="sm:grid-cols-2" />
          </Card>
          <Card>
            <CardHeader title="Périmètre" description="Tout le portefeuille ou quelques lignes" />
            <RadioCards ariaLabel="Périmètre de l’analyse" options={scopeOptions} value={fullPortfolio ? 'full' : 'selection'} onChange={(v) => setFullPortfolio(v === 'full')} className="sm:grid-cols-2" />
            {!fullPortfolio && (
              <div className="mt-5">
                <p className="eyebrow mb-3">Valeurs à analyser</p>
                <div className="flex flex-wrap gap-2">
                  {positions.map((p) => {
                    const selected = selectedSymbols.includes(p.symbol);
                    return (
                      <button
                        key={p.position_id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => toggleSymbol(p.symbol)}
                        className={cx(
                          'inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors duration-150',
                          selected ? 'border-gold-500/50 bg-gold-500/15 text-gold-200' : 'border-line bg-surface-2/40 text-fg-muted hover:border-line-strong hover:text-fg',
                        )}
                      >
                        {selected && <Check size={14} aria-hidden="true" />}
                        {p.symbol}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6 lg:sticky lg:top-[calc(var(--topbar-h)+1.5rem)] lg:self-start">
          <Card tone="gold">
            <CardHeader title="Récapitulatif" />
            <dl className="divide-y divide-line">
              <Fact label="Type">{typeLabel}</Fact>
              <Fact label="Périmètre">{fullPortfolio ? 'Portefeuille complet' : `${selectedSymbols.length} valeur(s)`}</Fact>
              <Fact label="Fournisseur">{PROVIDERS[settings?.ai_provider] || settings?.ai_provider || 'Non configuré'}</Fact>
              <Fact label="Modèle">{settings?.ai_model || 'Non configuré'}</Fact>
            </dl>
            <Button
              size="lg"
              fullWidth
              leadingIcon={Sparkles}
              loading={loading}
              disabled={!hasApiKey || (!fullPortfolio && selectedSymbols.length === 0)}
              onClick={handleGenerate}
              className="mt-6"
            >
              {loading ? 'Analyse en cours…' : 'Lancer l’analyse'}
            </Button>
            <p className="mt-3 text-center text-xs text-fg-subtle">Jusqu’à une minute selon le modèle.</p>
          </Card>
          <Card>
            <CardHeader title="Ce que vous obtiendrez" />
            <ul className="space-y-2.5">
              {BENEFITS.map((benefit) => (
                <li key={benefit} className="flex items-start gap-2.5 text-sm text-fg-body">
                  <Check size={16} aria-hidden="true" className="mt-0.5 flex-shrink-0 text-gold-400" />
                  {benefit}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title="Analyse IA"
        description="Des analyses à la demande de votre portefeuille, rédigées par l’IA."
        actions={(
          <>
            <Button as={Link} to="/reports" variant="ghost" leadingIcon={FileText}>Rapports quotidiens</Button>
            <Button variant="secondary" leadingIcon={showHistory ? Sparkles : Clock} onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Nouvelle analyse' : 'Historique'}
            </Button>
            <Button leadingIcon={MessageCircle} onClick={() => openChat()}>Parler à Gladys</Button>
          </>
        )}
      />
      <BrokerGate>
        <div className="space-y-6">
          {!hasApiKey && (
            <Banner tone="warn" title="Clé API requise" action={<Button as={Link} to="/settings" variant="secondary" size="sm">Ouvrir les réglages</Button>}>
              Configurez votre clé OpenAI ou Gemini dans les réglages pour générer des analyses.
            </Banner>
          )}
          {error && <Banner tone="loss" role="alert">{error}</Banner>}
          {body}
        </div>
      </BrokerGate>
    </div>
  );
};

export default Analysis;
